import { Text } from '@uhg-abyss/web/ui/Text';
import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { Directions } from '../../models/RouteDirections';
import {
  ArrowContainer,
  DetailsContainer,
  DistanceContainer,
  DistanceContainerWidget,
  DurationDistanceWrapper,
  DuratonContainer,
  HorizontalDivider,
  RouteDetailsContainer,
  RouteInstructionsContainer,
  RoutesContainer,
  RoutesTab,
  RoutesTabContainer,
  SeeDirectionsContainer,
  StepContainer,
  StepDataContainer,
  StepIconContainer,
  StepsContainter,
  StepsWrapper,
  TripDurationContainer,
  TripLocationContainer,
  TripLocationDataContainer,
  VerticalDivider,
} from './MapDisplay.styled';
import { NavigationButtonsRow } from './Navigation/NavigationButtonsRow';

type Props = {
  currentRoute: number;
  getDistanceString(distance: number | undefined): string;
  getDurationString(
    t: TFunction,
    duration: number | undefined,
    isSecondRequired?: boolean
  ): string;
  getNavTypeString(navType: string): string;
  getStepDistanceString(distance: number): string;
  getStepIcon: any;
  handleCloseNav(): void;
  latitude: string;
  loadRoute: any;
  longitude: string;
  map: any;
  mapContainerRef: any;
  mapContainerTestId: string;
  mapDirections: Directions;
  mobileScreen: boolean;
  navigationContainerRef: React.MutableRefObject<
    HTMLElement | null | undefined
  >;
  navType: string;
  routeEndCoords: [number | null, number | null];
  setCurrentRoute(index: number): void;
  setNavType(navType: string): void;
  setShowStepInstructions(show: boolean): void;
  showStepInstructions: boolean;
};

export const MapDirections = ({
  currentRoute,
  getDistanceString,
  getDurationString,
  getNavTypeString,
  getStepDistanceString,
  getStepIcon,
  handleCloseNav,
  latitude,
  loadRoute,
  longitude,
  map,
  mapContainerRef,
  mapContainerTestId,
  mapDirections,
  mobileScreen,
  navigationContainerRef,
  navType,
  routeEndCoords,
  setCurrentRoute,
  setNavType,
  setShowStepInstructions,
  showStepInstructions,
}: Props) => {
  const { t } = useTranslation();
  const isWidget = useStore(StoreKeys.IS_WIDGET);

  useEffect(() => {
    if (navigationContainerRef)
      navigationContainerRef.current = document.getElementById(
        'driving-traffic'
      ) as HTMLElement;
  }, []);
  return (
    <React.Fragment>
      <NavigationButtonsRow
        handleCloseNav={handleCloseNav}
        locationForAnalytics={mapContainerTestId}
        navType={navType}
        setNavType={setNavType}
      />
      {!isWidget ? (
        <React.Fragment>
          <TripLocationContainer>
            <TripLocationDataContainer>
              <Text
                css={{
                  color: '$core.color.neutral.90',
                  fontSize: '$smallText',
                  fontWeight: 'bold',
                  lineHeight: '$sm',
                  '@media (max-width: 464px)': {
                    fontWeight: '600',
                  },
                }}
              >
                A. {t('DISPLAY_DIRECTIONS.START_LOCATION')}
              </Text>
              <Text
                css={{
                  color: '$core.color.neutral.90',
                  fontSize: '$md',
                  fontWeight: '$medium',
                  lineHeight: '$md',
                  textWrap: 'nowrap',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {mapDirections.userPlaceName}
              </Text>
            </TripLocationDataContainer>
            <TripLocationDataContainer>
              <Text
                css={{
                  color: '$core.color.neutral.90',
                  fontSize: '$smallText',
                  fontWeight: 'bold',
                  lineHeight: '$sm',
                  '@media (max-width: 464px)': {
                    fontWeight: '600',
                  },
                }}
              >
                B. {t('DISPLAY_DIRECTIONS.DESTINATION')}
              </Text>
              <Text
                css={{
                  color: '$core.color.neutral.90',
                  fontSize: '$md',
                  fontWeight: '$medium',
                  lineHeight: '$md',
                  textWrap: 'nowrap',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {mapDirections.endPlaceName}
              </Text>
            </TripLocationDataContainer>
          </TripLocationContainer>
          <HorizontalDivider />
        </React.Fragment>
      ) : null}
      <TripDurationContainer cssProps={{ isWidget }}>
        <DurationDistanceWrapper
          cssProps={{ isWidget }}
          data-testid={`distance-duration-${navType}`}
        >
          <DuratonContainer>
            {getDurationString(t, mapDirections.routes[currentRoute]?.duration)}{' '}
            {t(getNavTypeString(navType))}
          </DuratonContainer>
          {!isWidget ? (
            <DistanceContainer>
              ({getDistanceString(mapDirections.routes[currentRoute]?.distance)}
              )
            </DistanceContainer>
          ) : (
            <DistanceContainerWidget>
              ({getDistanceString(mapDirections.routes[currentRoute]?.distance)}
              )
            </DistanceContainerWidget>
          )}
        </DurationDistanceWrapper>

        <SeeDirectionsContainer
          aria-expanded={showStepInstructions}
          data-auto-testid="see-directions"
          data-testid="see-directions"
          onClick={() => {
            setShowStepInstructions(!showStepInstructions);
          }}
          role="button"
          tabIndex="0"
        >
          <Text
            css={{
              fontWeight: showStepInstructions ? 'bold' : 'normal',
              fontSize: showStepInstructions ? '12.64px' : '$sm',
              lineHeight: showStepInstructions ? '16px' : '18px',
              textWrap: 'nowrap',
              color: '$core.color.brand.60',
            }}
          >
            {isWidget
              ? t('DISPLAY_DIRECTIONS.GET_DIRECTIONS')
              : t('DISPLAY_DIRECTIONS.SEE_DIRECTIONS')}
          </Text>
          <ArrowContainer>
            {showStepInstructions ? (
              <svg
                fill="none"
                height="18"
                viewBox="0 0 18 18"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clipRule="evenodd"
                  d="M9 6L4.5 10.5L5.5575 11.5575L9 8.1225L12.4425 11.5575L13.5 10.5L9 6Z"
                  fill="$core.color.brand.60"
                  fillRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                fill="none"
                height="18"
                viewBox="0 0 18 18"
                width="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clipRule="evenodd"
                  d="M12.4425 6.44238L9 9.87738L5.5575 6.44238L4.5 7.49988L9 11.9999L13.5 7.49988L12.4425 6.44238Z"
                  fill="$core.color.brand.60"
                  fillRule="evenodd"
                />
              </svg>
            )}
          </ArrowContainer>
        </SeeDirectionsContainer>
      </TripDurationContainer>
      <RouteInstructionsContainer
        css={{ display: showStepInstructions ? 'block' : 'none' }}
        data-testid="route-details-container"
      >
        <HorizontalDivider />

        <RouteDetailsContainer>
          <DetailsContainer>
            <Text
              css={{
                display: 'block',
                fontSize: '$md',
                color: '$core.color.neutral.90',
                fontWeight: 'bold',
                lineHeight: '$md',
              }}
            >
              {getDurationString(
                t,
                mapDirections.routes[currentRoute]?.duration
              )}
            </Text>
            <Text
              css={{
                color: '$core.color.neutral.90',
                marginTop: '$xs',
                display: 'block',
                fontSize: '$md',
                fontWeight: '$medium',
                lineHeight: '$md',
              }}
            >
              {getDistanceString(mapDirections?.routes[currentRoute]?.distance)}
            </Text>
          </DetailsContainer>
        </RouteDetailsContainer>

        <RoutesContainer>
          <Text
            css={{
              color: '$core.color.neutral.90',
              fontSize: '$md',
              fontWeight: 'bold',
              lineHeight: '$md',
            }}
            id="routes"
          >
            {t('DISPLAY_DIRECTIONS.ROUTES')}
          </Text>
          <RoutesTabContainer aria-labelledby="routes" role="group">
            {mapDirections?.routes?.map((_, index: number) => (
              <React.Fragment key={index}>
                <RoutesTab
                  aria-pressed={currentRoute === index}
                  css={{
                    background:
                      index === currentRoute ? '$core.color.brand.60' : 'none',
                    color:
                      index === currentRoute
                        ? 'white'
                        : '$core.color.neutral.60',
                    fontFamily: '$primary',
                  }}
                  data-auto-testid={`routes-tab-${index}`}
                  data-testid={`routes-tab-${index}`}
                  key={`routes-tab-${index}`}
                  onClick={() => {
                    setCurrentRoute(index);
                    loadRoute(
                      map.current,
                      mapDirections?.routes,
                      index,
                      longitude,
                      latitude,
                      routeEndCoords[0],
                      routeEndCoords[1]
                    );
                  }}
                >
                  {index + 1}
                </RoutesTab>
                {index !== mapDirections.routes.length - 1 && (
                  <VerticalDivider />
                )}
              </React.Fragment>
            ))}
          </RoutesTabContainer>
        </RoutesContainer>
        <StepsContainter
          cssProps={{
            mapContainerHeight: mapContainerRef?.current?.clientHeight,
          }}
          tabIndex="0"
        >
          <Text
            css={{
              display: 'block',
              padding: '$md 0',
              fontWeight: 'bold',
              lineHeight: '$md',
              color: '$core.color.neutral.90',
              fontSize: '$md',
            }}
          >
            {t('DISPLAY_DIRECTIONS.STEPS')}
          </Text>
          <StepsWrapper>
            {!mobileScreen && (
              <StepContainer>
                <StepIconContainer>A</StepIconContainer>
                <StepDataContainer>
                  <Text
                    css={{
                      color: '$core.color.neutral.90',
                      display: 'block',
                      fontSize: '$smallText',
                      lineHeight: '$md',
                      fontWeight: 'bold',
                    }}
                  >
                    {t('DISPLAY_DIRECTIONS.START_LOCATION')}
                  </Text>
                  <Text
                    css={{
                      color: '$neutralGray5',
                      display: 'block',
                      fontSize: '$smallText',
                      lineHeight: '$md',
                      fontWeight: '$medium',
                    }}
                    data-testid={`start-location`}
                  >
                    {mapDirections.userPlaceName}
                  </Text>
                </StepDataContainer>
              </StepContainer>
            )}

            {mapDirections.routes?.[currentRoute]?.steps?.map(
              (
                { distance, maneuver: { modifier, instruction, type } },
                index
              ) => (
                <StepContainer key={`step-container-${index}`}>
                  <StepIconContainer>
                    <img
                      alt=""
                      aria-hidden="true"
                      src={`data:image/svg+xml,${encodeURIComponent(
                        getStepIcon(modifier, type)
                      )}`}
                    />
                  </StepIconContainer>
                  <StepDataContainer>
                    <Text
                      css={{
                        color:
                          (mobileScreen && index === 0) ||
                          index ===
                            (mapDirections?.routes?.[currentRoute]?.steps
                              ?.length as number) -
                              1
                            ? '$core.color.neutral.90'
                            : '$core.color.neutral.80',
                        display: 'block',
                        fontSize: '$smallText',
                        lineHeight: '$md',
                        fontWeight:
                          (mobileScreen && index === 0) ||
                          index ===
                            (mapDirections?.routes?.[currentRoute]?.steps
                              ?.length as number) -
                              1
                            ? 'bold'
                            : '600',
                      }}
                    >
                      {instruction}
                    </Text>
                    <Text
                      css={{
                        color: '$neutralGray5',
                        display: 'block',
                        fontSize: '$smallText',
                        lineHeight: '$md',
                        fontWeight: '$medium',
                      }}
                    >
                      {getStepDistanceString(distance)}
                    </Text>
                  </StepDataContainer>
                </StepContainer>
              )
            )}

            {!mobileScreen && (
              <StepContainer>
                <StepIconContainer>B</StepIconContainer>
                <StepDataContainer>
                  <Text
                    css={{
                      color: '$core.color.neutral.90',
                      display: 'block',
                      fontSize: '$smallText',
                      lineHeight: '$md',
                      fontWeight: 'bold',
                    }}
                  >
                    {t('DISPLAY_DIRECTIONS.DESTINATION')}
                  </Text>
                  <Text
                    css={{
                      color: '$neutralGray5',
                      display: 'block',
                      fontSize: '$smallText',
                      lineHeight: '$md',
                      fontWeight: '$medium',
                    }}
                    data-testid={`destination-location`}
                  >
                    {mapDirections.endPlaceName}
                  </Text>
                </StepDataContainer>
              </StepContainer>
            )}
          </StepsWrapper>
        </StepsContainter>
      </RouteInstructionsContainer>
    </React.Fragment>
  );
};
